import React from "react";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { Button, Chip } from "@mui/material";

import { TableCell, ModalRef } from "@shared/components";
import { Company } from "@modules/companies/types";
import { RefObject } from "react";

export const useReferralListColumns = (
  translate: (key: string) => string,
  payModalRef: RefObject<ModalRef>,
  companies?: Company[],
): GridColDef[] => {
  return React.useMemo<GridColDef[]>(
    () => [
      {
        field: "id",
        headerName: translate("referral_admin.fields.id"),
        minWidth: 220,
      },
      {
        field: "agreement",
        headerName: translate("referral_admin.fields.agreement"),
        minWidth: 220,
        renderCell: ({ value }) => (
          <TableCell.Link
            to={`/agreement/edit/${value?.id}`}
            value={value?.name || "-"}
          />
        ),
      },
      {
        field: "document",
        headerName: translate("referral_admin.fields.document"),
        minWidth: 140,
        renderCell: ({ value }) => (
          <TableCell.Text value={value} />
        ),
      },
      {
        field: "name",
        headerName: translate("referral_admin.fields.name"),
        minWidth: 200,
        renderCell: ({ value }) => (
          <TableCell.Text value={value || "-"} />
        ),
      },
      {
        field: "email",
        headerName: translate("referral_admin.fields.email"),
        minWidth: 220,
        renderCell: ({ value }) => (
          <TableCell.Text value={value || "-"} />
        ),
      },
      {
        field: "telephone",
        headerName: translate("referral_admin.fields.telephone"),
        minWidth: 140,
        renderCell: ({ value }) => (
          <TableCell.Text value={value || "-"} />
        ),
      },
      {
        field: "status",
        headerName: translate("referral_admin.fields.status"),
        minWidth: 120,
        renderCell: ({ value }) => {
          const status = value as string;
          const color: "default" | "primary" | "success" | "error" | "warning" =
            status === "paid"
              ? "success"
              : status === "confirmed"
              ? "primary"
              : status === "expired"
              ? "warning"
              : "default";

          return (
            <Chip
              label={translate(`referral_admin.status.${status}`)}
              color={color}
              size="small"
              variant="outlined"
            />
          );
        },
      },
      {
        field: "value_paid",
        headerName: translate("referral_admin.fields.value_paid"),
        minWidth: 120,
        headerAlign: "right",
        renderCell: ({ value }) => (
          <TableCell.Text
            value={value ? `R$ ${parseFloat(value).toFixed(2)}` : "-"}
            textAlign="right"
          />
        ),
      },
      {
        field: "created_from",
        headerName: translate("referral_admin.fields.created_from"),
        minWidth: 140,
        renderCell: ({ value }) => {
          const origin = value as string;
          const colorMap: Record<string, "primary" | "secondary" | "info"> = {
            app: "primary",
            landing: "secondary",
            api: "info",
          };
          const color = colorMap[origin] ?? "default";

          return (
            <Chip
              label={translate(`referral_admin.origin.${origin}`, origin)}
              color={color}
              size="small"
            />
          );
        },
      },
      {
        field: "created_at",
        headerName: translate("referral_admin.fields.created_at"),
        minWidth: 160,
        renderCell: ({ value }) => (
          <TableCell.Date value={value} />
        ),
      },
      {
        field: "actions",
        headerName: translate("table.actions"),
        headerAlign: "center",
        minWidth: 120,
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams) => (
          <TableCell.Container align="center">
            <Button
              variant="outlined"
              size="small"
              color="success"
              disabled={row.status !== "confirmed"}
              onClick={() => payModalRef.current?.open({ id: row.id })}
            >
              {translate("referral_admin.buttons.pay")}
            </Button>
          </TableCell.Container>
        ),
      },
    ],
    [translate, companies, payModalRef],
  );
};

